import React, { useCallback } from "react";
import { Alert, Button, Card, Form, message } from "antd";
import { ApartmentOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ActionPlanAiPendingPlan, ActionPlanAiPendingState } from "@/types/action-plan-v2/ActionPlanAiPlan";
import { ActionPlanAiDraftFields } from "@/components/common/ActionPlanV2/actionPlanAiDraftFields";
import {
  draftFormValuesToPlan,
  draftFormValuesToUpdatePlan,
  validateActionPlanDraftPlan,
} from "@/components/common/ActionPlanV2/actionPlanAiDraftScope";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import { useAuthStore } from "@/store/useAuthStore";
import { usePrivateStore } from "@/store/usePrivateStore";
import styles from "./AiChatView.module.css";
import cardStyles from "./ActionPlanPendingCard.module.css";
import apCard from "./apCard.module.css";

type Props = {
  pending: ActionPlanAiPendingState;
  onConfirm: (plan: ActionPlanAiPendingPlan) => void;
  onDismiss: () => void;
  onPlanChange?: (plan: ActionPlanAiPendingPlan) => void;
  /** Việc con hàng loạt: xác nhận lần lượt mọi thẻ còn lại (tránh bấm Xác nhận từng thẻ). */
  onConfirmAll?: () => void;
  /** Gửi câu vào chat (đổi người phụ trách…) — trợ lý hỏi lại theo rule R03/R04. */
  onAsk?: (text: string) => void;
};

export const ActionPlanPendingCard: React.FC<Props> = ({
  pending,
  onConfirm,
  onDismiss,
  onPlanChange,
  onConfirmAll,
  onAsk,
}) => {
  const { plan, status, error } = pending;
  const [form] = Form.useForm();

  const showDraftForm =
    (plan.action === "create" || plan.action === "update") && plan.draftForm !== false;

  const handleConfirm = useCallback(async () => {
    if (!showDraftForm) {
      onConfirm(plan);
      return;
    }
    try {
      const values = await form.validateFields();
      const selectTree = usePrivateStore.getState().getRequestHeader().selectTree;
      const profileId = useAuthStore.getState().profile?.id;
      const scope = {
        idChoice: String(selectTree?.idSelected || plan.idChoice || profileId || "").trim(),
        year: plan.year ?? usePrivateStore.getState().giaiDoan?.year ?? dayjs().year(),
        phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
      };
      const merged =
        plan.action === "update"
          ? draftFormValuesToUpdatePlan(plan, values, scope)
          : draftFormValuesToPlan(plan, values, scope);
      const validationError = validateActionPlanDraftPlan(merged);
      if (validationError) {
        message.warning(validationError);
        return;
      }
      onPlanChange?.(merged);
      onConfirm(merged);
    } catch {
      /* antd validation */
    }
  }, [form, onConfirm, onPlanChange, plan, showDraftForm]);

  if (status === "dismissed") return null;
  if (plan.mustManual || plan.action === "manual") {
    return (
      <Alert
        type="warning"
        showIcon
        message="Cần thao tác thủ công"
        description={plan.reason || "Hãy thực hiện trực tiếp trên Action Plan."}
        className={styles.errorAlert}
      />
    );
  }

  const isDelete = plan.action === "delete";
  let title = plan.action === "create" ? "Tạo việc mới" : isDelete ? "Xoá việc" : "Cập nhật việc";
  if (plan.breakdownTotal && plan.breakdownIndex) {
    title = `Việc con (${plan.breakdownIndex}/${plan.breakdownTotal})`;
  } else if (plan.parentId && plan.action === "create") {
    title = "Tạo việc con";
  }
  const readOnly = status === "confirming" || status === "done";
  const total = plan.breakdownTotal ?? 0;
  const index = plan.breakdownIndex ?? 0;
  const remaining = total > 1 && index > 0 ? total - index + 1 : 0;
  const due = plan.ngayKetThuc ? dayjs(String(plan.ngayKetThuc).slice(0, 10)) : null;

  return (
    <Card
      size="small"
      title={
        <span className={cardStyles.headTitle}>
          <span className={`${cardStyles.headIcon} ${isDelete ? cardStyles.headIconDanger : ""}`} aria-hidden>
            {isDelete ? <DeleteOutlined /> : plan.action === "update" ? <EditOutlined /> : plan.parentId ? <ApartmentOutlined /> : <PlusOutlined />}
          </span>
          {title}
        </span>
      }
      className={cardStyles.card}
    >
      {total > 1 ? (
        <div className={apCard.steps} aria-label={`Việc con ${index}/${total}`}>
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className={`${apCard.step} ${
                i + 1 < index || (i + 1 === index && status === "done")
                  ? apCard.stepDone
                  : i + 1 === index
                    ? apCard.stepCurrent
                    : ""
              }`}
            />
          ))}
        </div>
      ) : null}
      {showDraftForm && status === "done" && plan.action !== "delete" ? (
        // Đã lưu: tóm tắt chỉ đọc đúng thứ đã ghi (trạng thái trung thực, không còn form)
        <dl className={cardStyles.summary}>
          <dt>Tên việc</dt>
          <dd>{plan.tenCongViec || "—"}</dd>
          {plan.nguoiPhuTrachTen ? (
            <>
              <dt>Phụ trách</dt>
              <dd>
                {plan.nguoiPhuTrachTen}
                {plan.nguoiPhuTrachBoPhan ? ` — ${plan.nguoiPhuTrachBoPhan}` : ""}
              </dd>
            </>
          ) : null}
          {due?.isValid() ? (
            <>
              <dt>Hạn</dt>
              <dd>{due.format("DD/MM/YYYY")}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {showDraftForm && status !== "done" ? (
        <Form form={form} layout="vertical" disabled={readOnly} requiredMark={false} size="small">
          <ActionPlanAiDraftFields plan={plan} form={form} disabled={readOnly} onAsk={onAsk} />
        </Form>
      ) : !showDraftForm && plan.tenCongViec ? (
        <div className="text-[13px] font-medium">{plan.tenCongViec}</div>
      ) : null}
      {isDelete && status !== "done" ? (
        <Alert
          type="warning"
          showIcon
          className="!mt-2 !text-xs"
          message={
            (plan.soViecCon ?? 0) > 0
              ? `Sẽ xoá kèm ${plan.soViecCon} việc con${plan.coTienDo ? " và toàn bộ tiến độ đã báo cáo" : ""}.`
              : plan.coTienDo
                ? "Sẽ xoá kèm toàn bộ tiến độ đã báo cáo."
                : "Không thể hoàn tác sau khi xoá."
          }
        />
      ) : null}

      {status === "error" && error ? (
        <Alert type="error" showIcon message={error} className="!mt-2 !text-xs" />
      ) : null}
      {status === "done" ? (
        <Alert
          type="success"
          showIcon
          message={isDelete ? "Đã xoá công việc." : "Đã lưu công việc."}
          className="!mt-2 !text-xs"
        />
      ) : null}

      {status === "pending" || status === "confirming" ? (
        <div className={cardStyles.actions}>
          <Button size="small" type="text" onClick={onDismiss} disabled={status === "confirming"}>
            Bỏ qua
          </Button>
          {remaining > 1 && onConfirmAll && status === "pending" ? (
            <Button size="small" onClick={onConfirmAll}>
              Xác nhận tất cả ({remaining})
            </Button>
          ) : null}
          <Button
            type="primary"
            danger={isDelete}
            size="small"
            loading={status === "confirming"}
            onClick={() => void handleConfirm()}
            aria-label={isDelete ? "Xoá công việc trên Action Plan" : "Xác nhận thay đổi Action Plan"}
          >
            {/* Nút chính ghi đúng động từ (thẻ xác nhận hiện tác động thật) */}
            {isDelete ? "Xoá" : plan.action === "update" ? "Lưu thay đổi" : plan.parentId ? "Tạo việc con" : "Tạo việc"}
          </Button>
        </div>
      ) : null}
    </Card>
  );
};
